// components/ProjectsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Button, List, ListItem, ListItemButton, ListItemText, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TextField, CircularProgress, Alert, Chip, IconButton, Tooltip, MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { TESTING_TYPE, normalizeTestingType, testingTypeLabel } from '../utils/testingType';

export default function ProjectsPage() {
  const { apiClient, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [name, setName] = useState('');
  const [testingType, setTestingType] = useState(TESTING_TYPE.MANUAL);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes('ADMIN');

  const load = () => {
    setLoading(true);
    setErr(null);
    apiClient.get('/api/projects')
      .then(res => setProjects(res.data || []))
      .catch(e => setErr((e && e.response && e.response.data && e.response.data.error) ? e.response.data.error : e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [apiClient]);

  const onCreate = () => {
    if (!name.trim()) return;
    setCreating(true);
    apiClient.post('/api/projects', {
      name: name.trim(),
      projectType: 'REQUIREMENTS_ONLY',
      webProjectSubtype: null,
      testingType: normalizeTestingType(testingType),
    })
      .then(() => {
        setDlgOpen(false);
        setName('');
        setTestingType(TESTING_TYPE.MANUAL);
        load();
      })
      .catch(e => setErr((e && e.response && e.response.data && e.response.data.error) ? e.response.data.error : e.message))
      .finally(() => setCreating(false));
  };

  const onDelete = () => {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    apiClient.delete(`/api/projects/${deleteTarget.id}`)
      .then(() => {
        setDeleteTarget(null);
        load();
      })
      .catch(e => setErr((e && e.response && e.response.data && e.response.data.error) ? e.response.data.error : e.message))
      .finally(() => setDeleting(false));
  };

  const closeDeleteDialog = () => {
    if (!deleting) {
      setDeleteTarget(null);
    }
  };

  const sortedProjects = useMemo(() => {
    const toTimestamp = (value) => {
      if (!value) {
        return 0;
      }
      const timestamp = Date.parse(value);
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    return [...projects].sort((left, right) => {
      const rightTs = toTimestamp(right?.createdAt);
      const leftTs = toTimestamp(left?.createdAt);
      if (rightTs !== leftTs) {
        return rightTs - leftTs;
      }
      return String(left?.name || '').localeCompare(String(right?.name || ''), 'ru');
    });
  }, [projects]);

  return (
    <Stack spacing={2} className="stagger-children">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Проекты</Typography>
        <Button variant="contained" onClick={() => setDlgOpen(true)} className="interactive-lift">
          Создать проект
        </Button>
      </Stack>

      {err && <Alert severity="error">{err}</Alert>}
      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
      ) : (
        <List>
          {projects.length === 0 && (
            <Typography color="text.secondary" sx={{ px: 2, py: 1 }}>
              Пока нет ни одного проекта.
            </Typography>
          )}
          {sortedProjects.map(p => (
            <ListItem
              key={p.id}
              disablePadding
              secondaryAction={
                <Tooltip title="Удалить проект">
                  <IconButton
                    edge="end"
                    aria-label="delete project"
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteTarget(p);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemButton
                divider
                onClick={() => navigate(`/projects/${p.id}`)}
                sx={{
                  pr: 9,
                  borderRadius: 2,
                  transition: 'transform 170ms ease, background-color 170ms ease',
                  '&:hover': {
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <ListItemText
                  primary={p.name}
                  secondary={
                    <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" rowGap={0.5}>
                      <Chip size="small" label={`Файлы: ${p.filesCount || 0}`} />
                      <Chip size="small" label={`Требования: ${p.requirementsCount || 0}`} />
                      <Chip size="small" label={testingTypeLabel(p?.testingType)} color="info" variant="outlined" />
                      {isAdmin && (
                        <Chip
                          size="small"
                          label={`Создатель: ${p.ownerUsername || 'неизвестно'}`}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
    )}

      <Dialog open={dlgOpen} onClose={() => !creating && setDlgOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Создать проект</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Название проекта"
            value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onCreate()} margin="normal"
          />
          <TextField
            select
            fullWidth
            label="Тип проекта"
            margin="normal"
            value={testingType}
            onChange={(event) => setTestingType(event.target.value)}
          >
            <MenuItem value={TESTING_TYPE.MANUAL}>Ручное тестирование</MenuItem>
            <MenuItem value={TESTING_TYPE.LOAD}>Нагрузочное тестирование</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)} disabled={creating}>Отмена</Button>
          <Button
            onClick={onCreate}
            variant="contained"
            disabled={creating || !name.trim()}
          >
            {creating ? 'Создаю…' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Удалить проект</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget ? `Действительно удалить проект "${deleteTarget.name}"? Это действие нельзя отменить.` : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>Отмена</Button>
          <Button onClick={onDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Удаляю…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
